import { ethers } from "ethers";
import { getHandleRegistryReadProvider } from "./base-read-provider";
import {
  HANDLE_HOME_NETWORK,
  HANDLE_PAGE_NETWORK,
  HANDLE_REGISTRY_CHAIN_ID,
  handleNetworkLabel,
  handleRegistryAddress,
} from "./handle-networks";
import { withDeadline } from "./withDeadline";

export type HandleChainScope = "base" | "any";
export type HandleRegistryScope = Exclude<HandleChainScope, "any">;

/** One network's receiving keys inside a handle record. */
export interface HandleEntry {
  network: string;
  recipientKey: string;
  mpk: string;
  inboxPublicKey: string;
}

const HANDLE_RECORD_FORMAT_V2 = 2;

/** A handle that receives on several networks; the v2 record hash commits
 * every entry, and the registry commits that hash. */
export interface HandleRecordV2 {
  format: typeof HANDLE_RECORD_FORMAT_V2;
  handle: string;
  chainScope: "any";
  salt: string;
  version: number;
  expiresAt: number;
  nonce: string;
  envId: string;
  entries: HandleEntry[];
  signature: string;
  recordHash: string;
}

/** The original single-network record (its network is the home network). */
export interface HandlePayload {
  handle: string;
  chainScope: HandleChainScope;
  recipientKey: string;
  mpk: string;
  inboxPublicKey: string;
  salt: string;
  version: number;
  expiresAt: number;
  nonce: string;
  envId: string;
  signature: string;
  recordHash: string;
}

export type HandleRecord = HandlePayload | HandleRecordV2;

/** What the registry actually stores. Read from chain, never from the resolver. */
export interface OnchainHandleRecord {
  recordCommitment: string;
  epoch: number;
  exists: boolean;
  ownerCommitment?: string;
}

export type HandleStateReader = (
  handleId: string,
  scope: HandleRegistryScope,
) => Promise<OnchainHandleRecord>;

export interface ResolveHandleOptions {
  readHandleState?: HandleStateReader;
  signal?: AbortSignal;
  /** The network the payment is made on; defaults to this page's. */
  network?: string;
}

export interface ResolvedHandleRecord {
  handle: string;
  chainScope: HandleChainScope;
  recipientKey: string;
  mpkHex?: string;
  inboxPublicKeyHex?: string;
  version: number;
  expiresAt: number;
  /** The network whose entry was resolved, and every network the handle has. */
  network: string;
  networks: string[];
}

export interface DestinationHandleResolution {
  handle: string;
  chainScope: HandleChainScope;
  recipientKey: string;
  mpkHex: string;
  inboxPublicKeyHex: string;
  version: number;
  expiresAt: number;
}

/** The handle exists but its owner hasn't added this network yet. */
export class HandleNetworkUnavailableError extends Error {
  constructor(handle: string, network: string) {
    const label = handleNetworkLabel(network);
    super(
      `${handle} hasn't set up ${label} yet. Ask them to open StealthOS on ${label} once.`,
    );
    this.name = "HandleNetworkUnavailableError";
  }
}

const ZERO_BYTES32 = `0x${"0".repeat(64)}`;
const RECORD_DOMAIN = "stealth-record-v1";
const RECORD_DOMAIN_V2 = "stealth-record-v2";
const BN254_PRIME = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);
const HANDLE_REGISTRY_IFACE = new ethers.Interface([
  "function getHandleState(bytes32) view returns (tuple(bytes32 recordCommitment,bytes32 ownerCommitment,uint64 epoch,bool exists))",
]);

let poseidonPromise: Promise<any> | undefined;

async function getPoseidon(): Promise<any> {
  if (!poseidonPromise) {
    poseidonPromise = (async () => {
      const circomlib = (await import("circomlibjs")) as unknown as {
        buildPoseidon: () => Promise<any>;
      };
      return circomlib.buildPoseidon();
    })();
  }
  return poseidonPromise;
}

function toFieldHex(value: bigint): string {
  const reduced = ((value % BN254_PRIME) + BN254_PRIME) % BN254_PRIME;
  return `0x${reduced.toString(16).padStart(64, "0")}`;
}

/**
 * The chain id folded into handleId, ownerCommitment and recordCommitment at
 * registration: always the registry's (Base) chain, on every network's page.
 * It must be derived the same way here or the registry lookup silently
 * targets a different handle.
 */
function handleChainId(_scope: HandleRegistryScope): number {
  return HANDLE_REGISTRY_CHAIN_ID;
}

function emitTelemetry(event: string, details?: Record<string, unknown>) {
  try {
    console.info(`[telemetry] ${event}`, details || {});
  } catch {
    // no-op
  }
}

export function normalizeHandle(value: string): string | null {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw.includes(".") && !raw.endsWith(".stealth")) return null;
  const handle = raw.endsWith(".stealth") ? raw : `${raw}.stealth`;
  if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)?\.stealth$/.test(handle)) {
    return null;
  }
  const labels = handle.slice(0, -".stealth".length).split(".");
  if (labels.length < 1 || labels.length > 2) {
    return null;
  }
  for (const label of labels) {
    if (
      label.length < 2 ||
      label.length > 32 ||
      label.startsWith("-") ||
      label.endsWith("-")
    ) {
      return null;
    }
  }
  const joined = labels.join(".");
  if (joined.length < 3 || joined.length > 48) {
    return null;
  }
  return handle;
}

export function isStealthHandle(value: string): boolean {
  return normalizeHandle(value) !== null;
}

function normalizeBytes32(value: string): string {
  const raw = String(value || "").trim();
  const v = raw.startsWith("0x") ? raw : `0x${raw}`;
  if (!/^0x[0-9a-fA-F]{64}$/.test(v)) {
    throw new Error("Invalid bytes32 value in handle payload");
  }
  return v.toLowerCase();
}

export function normalizeHandlePayload(raw: any): HandlePayload {
  const handle = normalizeHandle(raw?.handle || "");
  if (!handle) {
    throw new Error("Invalid handle payload");
  }
  const chainScope = String(raw?.chainScope || "any").toLowerCase();
  if (!["base", "any"].includes(chainScope)) {
    throw new Error("Invalid handle chain scope");
  }
  const version = Number(raw?.version);
  const expiresAt = Number(raw?.expiresAt);
  if (!Number.isInteger(version) || version <= 0) {
    throw new Error("Invalid handle version");
  }
  if (!Number.isInteger(expiresAt) || expiresAt <= 0) {
    throw new Error("Invalid handle expiry");
  }
  return {
    handle,
    chainScope: chainScope as HandleChainScope,
    recipientKey: normalizeBytes32(raw?.recipientKey),
    mpk: normalizeBytes32(raw?.mpk || ZERO_BYTES32),
    inboxPublicKey: normalizeBytes32(raw?.inboxPublicKey || ZERO_BYTES32),
    salt: normalizeBytes32(raw?.salt),
    version,
    expiresAt,
    nonce: normalizeBytes32(raw?.nonce),
    envId: normalizeBytes32(raw?.envId),
    signature: String(raw?.signature || "").trim(),
    // recordHash is optional during payload construction (computed immediately after normalization).
    // It is required only for verification of resolved/persisted payloads.
    recordHash: raw?.recordHash
      ? normalizeBytes32(raw?.recordHash)
      : ZERO_BYTES32,
  };
}

export function computeHandleRecordHash(payload: HandlePayload): string {
  const encoded = ethers.AbiCoder.defaultAbiCoder().encode(
    [
      "string",
      "string",
      "string",
      "bytes32",
      "bytes32",
      "bytes32",
      "bytes32",
      "uint64",
      "uint64",
      "bytes32",
    ],
    [
      RECORD_DOMAIN,
      payload.handle,
      payload.chainScope,
      payload.recipientKey,
      payload.mpk,
      payload.inboxPublicKey,
      payload.salt,
      payload.version,
      payload.expiresAt,
      payload.envId,
    ],
  );
  return ethers.keccak256(encoded).toLowerCase();
}

export function isHandleRecordV2(record: unknown): record is HandleRecordV2 {
  return (record as { format?: unknown })?.format === HANDLE_RECORD_FORMAT_V2;
}

/** Entries sorted by network name, the order the record hash commits. */
export function sortHandleEntries(entries: HandleEntry[]): HandleEntry[] {
  return [...entries].sort((a, b) =>
    a.network < b.network ? -1 : a.network > b.network ? 1 : 0,
  );
}

// Network names are committed by the record hash. A well-formed name this
// build doesn't know (a network added later) must not hide the entries it
// does know; payment only ever uses this page's own network's entry.
const NETWORK_KEY = /^[a-z0-9][a-z0-9-]{1,31}$/;
const MAX_HANDLE_ENTRIES = 16;

function normalizeHandleEntry(raw: any): HandleEntry {
  const network = String(raw?.network || "").trim();
  if (!NETWORK_KEY.test(network)) {
    throw new Error("Invalid handle network");
  }
  return {
    network,
    recipientKey: normalizeBytes32(raw?.recipientKey),
    mpk: normalizeBytes32(raw?.mpk),
    inboxPublicKey: normalizeBytes32(raw?.inboxPublicKey),
  };
}

/** A v1 or v2 handle record, with the same checks the data API applies. */
export function normalizeHandleRecord(raw: any): HandleRecord {
  if (!isHandleRecordV2(raw)) return normalizeHandlePayload(raw);
  const handle = normalizeHandle(raw?.handle || "");
  if (!handle) throw new Error("Invalid handle payload");
  if (String(raw?.chainScope || "").toLowerCase() !== "any") {
    throw new Error("Invalid handle chain scope");
  }
  const version = Number(raw?.version);
  const expiresAt = Number(raw?.expiresAt);
  if (!Number.isInteger(version) || version <= 0) {
    throw new Error("Invalid handle version");
  }
  if (!Number.isInteger(expiresAt) || expiresAt <= 0) {
    throw new Error("Invalid handle expiry");
  }
  const rawEntries: unknown[] = Array.isArray(raw?.entries) ? raw.entries : [];
  if (!rawEntries.length || rawEntries.length > MAX_HANDLE_ENTRIES) {
    throw new Error("Invalid handle network entries");
  }
  const entries = rawEntries.map(normalizeHandleEntry);
  for (let i = 1; i < entries.length; i++) {
    if (entries[i - 1].network >= entries[i].network) {
      throw new Error("Handle network entries must be unique and sorted");
    }
  }
  return {
    format: HANDLE_RECORD_FORMAT_V2,
    handle,
    chainScope: "any",
    salt: normalizeBytes32(raw?.salt),
    version,
    expiresAt,
    nonce: normalizeBytes32(raw?.nonce),
    envId: normalizeBytes32(raw?.envId),
    entries,
    signature: String(raw?.signature || "").trim(),
    recordHash: raw?.recordHash
      ? normalizeBytes32(raw?.recordHash)
      : ZERO_BYTES32,
  };
}

export function computeHandleRecordHashV2(record: HandleRecordV2): string {
  const encoded = ethers.AbiCoder.defaultAbiCoder().encode(
    [
      "string",
      "string",
      "string",
      "bytes32",
      "uint64",
      "uint64",
      "bytes32",
      "tuple(string network,bytes32 recipientKey,bytes32 mpk,bytes32 inboxPublicKey)[]",
    ],
    [
      RECORD_DOMAIN_V2,
      record.handle,
      record.chainScope,
      record.salt,
      record.version,
      record.expiresAt,
      record.envId,
      record.entries.map((entry) => [
        entry.network,
        entry.recipientKey,
        entry.mpk,
        entry.inboxPublicKey,
      ]),
    ],
  );
  return ethers.keccak256(encoded).toLowerCase();
}

function computeAnyHandleRecordHash(record: HandleRecord): string {
  return isHandleRecordV2(record)
    ? computeHandleRecordHashV2(record)
    : computeHandleRecordHash(record);
}

/** What the resolver's signature covers; the registry commitment is what's trusted. */
export function serializeHandleRecordV2ForSigning(
  record: HandleRecordV2,
): string {
  return JSON.stringify({
    domain: RECORD_DOMAIN_V2,
    handle: record.handle,
    chainScope: record.chainScope,
    salt: record.salt,
    version: record.version,
    expiresAt: record.expiresAt,
    nonce: record.nonce,
    envId: record.envId,
    entries: record.entries,
  });
}

/** Receiving entries of a record; a v1 record is the home network's. */
export function handleRecordEntries(record: HandleRecord): HandleEntry[] {
  if (isHandleRecordV2(record)) return record.entries;
  return [
    {
      network: HANDLE_HOME_NETWORK,
      recipientKey: record.recipientKey,
      mpk: record.mpk,
      inboxPublicKey: record.inboxPublicKey,
    },
  ];
}

export async function computeHandleId(
  handle: string,
  chainId: number,
): Promise<string> {
  const poseidon = await getPoseidon();
  const labelHash = ethers.keccak256(ethers.toUtf8Bytes(handle));
  const hash = poseidon([BigInt(labelHash), BigInt(chainId)]);
  return toFieldHex(BigInt(poseidon.F.toObject(hash).toString()));
}

/**
 * The registry stores Poseidon(recordHash, epoch, chainId), not the record hash
 * itself, so a resolved payload can only be checked by recomputing this.
 */
export async function computeHandleRecordCommitment(
  recordHash: string,
  epoch: number,
  chainId: number,
): Promise<string> {
  const poseidon = await getPoseidon();
  const hash = poseidon([BigInt(recordHash), BigInt(epoch), BigInt(chainId)]);
  return toFieldHex(BigInt(poseidon.F.toObject(hash).toString()));
}

/** The registry's state for a handle, read from the Base chain on any page. */
async function readHandleRegistryState(
  handleId: string,
): Promise<OnchainHandleRecord & { ownerCommitment: string }> {
  const registry = handleRegistryAddress();
  if (!registry) {
    throw new Error(
      "REACT_APP_BASE_STEALTH_ANON_REGISTRY_ADDRESS is not configured; refusing to resolve a handle that cannot be checked on chain",
    );
  }
  const provider = getHandleRegistryReadProvider();
  const raw = await provider.call({
    to: registry,
    data: HANDLE_REGISTRY_IFACE.encodeFunctionData("getHandleState", [
      handleId,
    ]),
  });
  const [state] = HANDLE_REGISTRY_IFACE.decodeFunctionResult(
    "getHandleState",
    raw,
  );
  return {
    recordCommitment: String(state.recordCommitment).toLowerCase(),
    ownerCommitment: String(state.ownerCommitment).toLowerCase(),
    epoch: Number(state.epoch),
    exists: Boolean(state.exists),
  };
}

const defaultHandleStateReader: HandleStateReader = (handleId) =>
  readHandleRegistryState(handleId);

/**
 * Everything routing-relevant — recipientKey, mpk, inbox public key — is bound
 * into recordHash, and the registry commits to that hash. Checking the resolved
 * payload against the registry is therefore the only thing standing between a
 * malicious resolve endpoint and funds sent to attacker keys; the resolver's
 * own signature proves nothing, because the resolver would be the attacker.
 */
export async function verifyHandlePayloadOnChain(
  payload: HandleRecord,
  scope: HandleRegistryScope,
  readHandleState: HandleStateReader = defaultHandleStateReader,
  { allowExpired = false }: { allowExpired?: boolean } = {},
): Promise<void> {
  const now = Math.floor(Date.now() / 1000);
  if (!allowExpired && payload.expiresAt <= now) {
    throw new Error("Handle record has expired");
  }
  const recordHash = computeAnyHandleRecordHash(payload);
  if (recordHash !== payload.recordHash.toLowerCase()) {
    throw new Error("Handle payload hash mismatch");
  }
  const chainId = handleChainId(scope);
  const expectedEnvId = ethers
    .keccak256(ethers.toUtf8Bytes(String(chainId)))
    .toLowerCase();
  if (payload.envId !== expectedEnvId) {
    throw new Error("Handle payload is bound to a different chain");
  }
  const handleId = await computeHandleId(payload.handle, chainId);
  const onchain = await readHandleState(handleId, scope);
  if (!onchain.exists) {
    throw new Error("Handle is not registered on chain");
  }
  const expectedCommitment = await computeHandleRecordCommitment(
    recordHash,
    onchain.epoch,
    chainId,
  );
  if (expectedCommitment !== onchain.recordCommitment.toLowerCase()) {
    throw new Error("Handle payload does not match the on-chain record");
  }
}

function isScopeAllowed(
  payloadScope: HandleChainScope,
  requested: HandleChainScope,
): boolean {
  if (payloadScope === "any") return true;
  return payloadScope === requested;
}

function trimZeroRecord(value: string): string | undefined {
  const v = String(value || "").toLowerCase();
  if (v === ZERO_BYTES32) return undefined;
  return v;
}

export async function resolveStealthHandle(
  handleInput: string,
  requestedScope: HandleChainScope,
  options?: ResolveHandleOptions,
): Promise<ResolvedHandleRecord> {
  return withDeadline(
    (signal) =>
      resolveStealthHandleVerified(handleInput, requestedScope, {
        ...options,
        signal,
      }),
    "Could not verify this handle in time. Please try again.",
    options?.signal,
  );
}

async function resolveStealthHandleVerified(
  handleInput: string,
  requestedScope: HandleChainScope,
  options?: ResolveHandleOptions,
): Promise<ResolvedHandleRecord> {
  const handle = normalizeHandle(handleInput);
  if (!handle) {
    emitTelemetry("handle.resolve.error", { reason: "invalid_format" });
    throw new Error("Invalid .stealth handle format");
  }
  if (requestedScope !== "base") {
    // "any" names no registry, so there is nothing to verify the record
    // against. Resolving it would be trusting the endpoint outright.
    emitTelemetry("handle.resolve.error", { handle, reason: "unscoped" });
    throw new Error("Handle resolution requires the base chain scope");
  }
  try {
    const response = await fetch(
      `/api/handles/resolve?handle=${encodeURIComponent(handle)}&chainScope=${encodeURIComponent(requestedScope)}`,
      { signal: options?.signal },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      emitTelemetry("handle.resolve.error", {
        handle,
        reason: "http_error",
        status: response.status,
      });
      throw new Error(
        response.status === 404
          ? "This handle is not registered. Check the recipient and try again."
          : "Could not verify this handle. Please try again.",
      );
    }

    const payload = normalizeHandleRecord(data?.payload || {});
    // A valid registered record for another name must not redirect this request.
    if (payload.handle !== handle) {
      throw new Error("Resolved handle does not match the requested handle");
    }
    await verifyHandlePayloadOnChain(
      payload,
      requestedScope,
      options?.readHandleState,
    );
    if (!isScopeAllowed(payload.chainScope, requestedScope)) {
      emitTelemetry("handle.resolve.error", {
        handle,
        reason: "scope_mismatch",
        payloadScope: payload.chainScope,
        requestedScope,
      });
      throw new Error(
        `Handle is scoped to ${payload.chainScope} and cannot be used on ${requestedScope}`,
      );
    }

    // Pay to the keys the recipient derived for the network the payment is on.
    const network = options?.network ?? HANDLE_PAGE_NETWORK;
    const entries = handleRecordEntries(payload);
    const entry = entries.find((candidate) => candidate.network === network);
    if (!entry) throw new HandleNetworkUnavailableError(handle, network);
    const resolved = {
      handle: payload.handle,
      chainScope: payload.chainScope,
      recipientKey: entry.recipientKey.slice(2).toLowerCase(),
      mpkHex: trimZeroRecord(entry.mpk),
      inboxPublicKeyHex: trimZeroRecord(entry.inboxPublicKey),
      version: payload.version,
      expiresAt: payload.expiresAt,
      network: entry.network,
      networks: entries.map((candidate) => candidate.network),
    };
    emitTelemetry("handle.resolve.success", {
      handle: resolved.handle,
      version: resolved.version,
      chainScope: resolved.chainScope,
    });
    return resolved;
  } catch (error) {
    if (error instanceof Error) {
      emitTelemetry("handle.resolve.error", {
        handle,
        reason: "exception",
        message: error.message,
      });
    }
    throw error;
  }
}

/**
 * The handle's current record for its owner, verified against the registry.
 * Expired records are included (renewing one needs its entries); null means
 * the registry may hold the name but no record was ever published.
 */
export async function loadVerifiedHandleRecord(
  handle: string,
  options?: { readHandleState?: HandleStateReader; signal?: AbortSignal },
): Promise<HandleRecord | null> {
  const response = await fetch(
    `/api/handles/resolve?handle=${encodeURIComponent(handle)}&chainScope=base&expired=1`,
    { cache: "no-store", signal: options?.signal },
  );
  if (response.status === 404) return null;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      "Could not load this handle's current record. Please try again.",
    );
  }
  const record = normalizeHandleRecord(data?.payload || {});
  if (record.handle !== handle) {
    throw new Error("Resolved handle does not match the requested handle");
  }
  await verifyHandlePayloadOnChain(record, "base", options?.readHandleState, {
    allowExpired: true,
  });
  return record;
}

export async function resolveDestinationStealthHandle(
  handleInput: string,
  requestedScope: HandleRegistryScope,
  options?: ResolveHandleOptions,
): Promise<DestinationHandleResolution> {
  const resolved = await resolveStealthHandle(
    handleInput,
    requestedScope,
    options,
  );
  if (!resolved.mpkHex) {
    throw new Error("Resolved handle is missing destination MPK");
  }
  if (!resolved.inboxPublicKeyHex) {
    throw new Error("Resolved handle is missing destination inbox public key");
  }
  return {
    handle: resolved.handle,
    chainScope: resolved.chainScope,
    recipientKey: resolved.recipientKey,
    mpkHex: resolved.mpkHex,
    inboxPublicKeyHex: resolved.inboxPublicKeyHex,
    version: resolved.version,
    expiresAt: resolved.expiresAt,
  };
}
